import { v4 } from 'uuid';
import { tryCatch } from '@/utils/tryCatch';
import EventosService from '@/app/checkins/eventos/services.js';
import { ComponenteFactory } from '@/factories/ComponenteFactory';

export const STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM = {
    INATIVO: 'INATIVO',
    CARREGANDO: 'CARREGANDO',
    SUCESSO: 'SUCESSO',
    ERRO: 'ERRO',
};
Object.freeze(STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM);

export const STATUS_SALVAMENTO_ENUM = {
    INATIVO: 'INATIVO',
    SALVANDO: 'SALVANDO',
    SUCESSO: 'SUCESSO',
    ERRO: 'ERRO',
};
Object.freeze(STATUS_SALVAMENTO_ENUM);

export const STATUS_ATUALIZACAO_ENUM = {
    INATIVO: 'INATIVO',
    ATUALIZANDO: 'ATUALIZANDO',
    SUCESSO: 'SUCESSO',
    ERRO: 'ERRO',
};
Object.freeze(STATUS_ATUALIZACAO_ENUM);

export const STATUS_PREVIEW_ENUM = {
    INATIVO: 'INATIVO',
    ABRINDO: 'ABRINDO',
    ABERTO: 'ABERTO',
    ERRO: 'ERRO',
};
Object.freeze(STATUS_PREVIEW_ENUM);

/**
 * Cria uma função de busca recursiva para encontrar um componente em uma estrutura aninhada.
 * Esta é uma "Higher-Order Function" que atua como uma fábrica.
 *
 * @param {string} chave - A chave do objeto do componente a ser usada para a comparação (ex: 'componenteId', 'componente').
 * @returns {function({componente: Object, valor: any}): Object|null} Uma nova função de busca especializada.
 */
const criarBuscadorDeComponentePor = (chave) => {
    if (!chave || typeof chave !== 'string') {
        throw new Error('É necessário fornecer uma chave (string) para criar a função de busca.');
    }

    /**
     * Encontra um componente por um valor específico na chave pré-configurada.
     * Esta é a função que é retornada pela fábrica.
     * @param {Object} params - Parâmetros da função.
     * @param {Object} params.componente - O componente raiz para iniciar a busca.
     * @param {*} params.valor - O valor a ser encontrado.
     * @returns {Object|null} - O componente encontrado ou nulo.
     */
    const buscadorRecursivo = ({ componente, valor }) => {
        if (valor == null) throw new Error(`É necessário passar o valor para a busca pela chave "${chave}"`);
        if (componente == null) throw new Error('É necessário passar o componente raiz para iniciar a busca');

        if (componente[chave] === valor) {
            return componente;
        }

        if (componente.children && componente.children.length > 0) {
            for (const subComponente of componente.children) {
                const encontrado = buscadorRecursivo({ componente: subComponente, valor });
                if (encontrado) {
                    return encontrado;
                }
            }
        }

        return null;
    };

    return buscadorRecursivo;
};

const encontrarChildrenPorId = criarBuscadorDeComponentePor('componenteId');
const encontrarChildrenPorComponente = criarBuscadorDeComponentePor('componente');

/**
 * Remove um componente da estrutura hierárquica
 * @param {Object} params - Parâmetros da função
 * @param {Object} params.componente - Componente raiz
 * @param {string} params.id - ID do componente a ser removido
 * @returns {boolean} - True se removido com sucesso, false caso contrário
 */
const removerComponenteRecursivo = ({ componente, id }) => {
    if (componente.children != null && componente.children.length > 0) {
        const index = componente.children.findIndex((child) => child.componenteId === id);
        if (index !== -1) {
            componente.children.splice(index, 1);
            return true;
        }

        for (const subComponente of componente.children) {
            if (removerComponenteRecursivo({ componente: subComponente, id })) {
                return true;
            }
        }
    }
    return false;
};

/**
 * Faz merge profundo de objetos, dando preferência aos valores do segundo objeto. faz um merge profundo (deep merge) entre dois objetos, ou seja, ele combina os valores do objeto atual com os valores do objeto mudancas, dando preferência aos valores de mudancas quando houver conflito.
 * Explicação detalhada
 * Objetivo:
 * Unir dois objetos recursivamente, preservando as propriedades do objeto original (atual), mas sobrescrevendo ou adicionando as propriedades do objeto de mudanças (mudancas).
 * Se uma propriedade for um objeto, ele faz o merge também dentro desse objeto (por isso é "profundo").
 * @param {Object} atual - Objeto com estado atual
 * @param {Object} mudancas - Objeto com as mudanças
 * @returns {Object} - Objeto resultante do merge
 */
const mergeObjetosRecursivo = (atual, mudancas) => {
    if (mudancas == null) throw new Error('O segundo argumento não pode ser nulo.');

    if (mudancas === null || typeof mudancas !== 'object' || Array.isArray(mudancas)) {
        return mudancas;
    }

    if (atual === null || typeof atual !== 'object' || Array.isArray(atual)) {
        return { ...mudancas };
    }

    const resultado = { ...atual };

    for (const [chave, valor] of Object.entries(mudancas)) {
        if (typeof valor === 'object' && valor !== null && !Array.isArray(valor)) {
            resultado[chave] = mergeObjetosRecursivo(atual[chave], valor);
        } else {
            resultado[chave] = valor;
        }
    }

    return resultado;
};

function normalizarEstrutura(node) {
    if (!node) return null;

    const isNoInvalido =
        (!node.componente || node.componente === null) &&
        (node.componenteId === '00000000-0000-0000-0000-000000000000' || node.componenteId === 0 || node.componenteId === '0');

    if (isNoInvalido) {
        if (Array.isArray(node.children)) {
            return node.children.map(normalizarEstrutura).reduce((acc, curr) => {
                if (Array.isArray(curr)) {
                    acc.push(...curr);
                } else if (curr) {
                    acc.push(curr);
                }
                return acc;
            }, []);
        }
        return [];
    }

    let filhosNormalizados = [];
    if (Array.isArray(node.children)) {
        node.children.forEach((child) => {
            const normalizado = normalizarEstrutura(child);
            if (Array.isArray(normalizado)) {
                filhosNormalizados.push(...normalizado);
            } else if (normalizado) {
                filhosNormalizados.push(normalizado);
            }
        });
    }

    return {
        ...node,
        children: filhosNormalizados,
    };
}

const landingPageModule = {
    namespaced: true,
    state() {
        return {
            nomeEvento: null,
            estruturaPagina: {
                children: [],
            },
            metadadosPagina: {
                landingPageId: null,
                eventoId: null,
                titulo: 'Nova Página',
                seoConfig: {
                    palavrasChave: [],
                    descricao: '',
                    imgCompartilhamento: '',
                },
                urlPersonalizada: '',
                visibilidade: 'publica',
                dataHoraPublicacao: null,
                dataHoraDespublicacao: null,
                cadastroNoLocal: false,
            },
            idElementoSelecionado: null,
            statusSalvamento: STATUS_SALVAMENTO_ENUM.INATIVO,
            statusAtualizacao: STATUS_ATUALIZACAO_ENUM.INATIVO,
            statusPreview: STATUS_PREVIEW_ENUM.INATIVO,
            statusCarregamentoEditorLandingPage: STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.INATIVO,
            isDirty: false,
        };
    },
    mutations: {
        DEFINIR_DIRTY_STATE(estado, valor) {
            estado.isDirty = valor;
        },
        DEFINIR_STATUS_CARREGAMENTO_EDITOR(estado, status) {
            const statusPermitidos = Object.keys(STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM);
            if (!statusPermitidos.includes(status)) {
                throw new Error(`Status de carregamento do editor inválido: ${status}. Valores permitidos: ${statusPermitidos.join(', ')}`);
            }
            estado.statusCarregamentoEditorLandingPage = STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM[status];
        },
        DEFINIR_ITENS_SECAO(estado, { idSecao, itens }) {
            function atualizarChildrenPorNome(componente, nome, novosChildren) {
                if (componente.componente === nome) {
                    componente.children = novosChildren;
                    return true;
                }
                if (Array.isArray(componente.children)) {
                    for (const child of componente.children) {
                        if (atualizarChildrenPorNome(child, nome, novosChildren)) {
                            return true;
                        }
                    }
                }
                return false;
            }
            atualizarChildrenPorNome(estado.estruturaPagina, idSecao, itens);
        },
        DEFINIR_CHILDREN_POR_ID(estado, { idComponente, novaListaItens }) {
            const componente = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: idComponente });
            if (componente) {
                componente.children = novaListaItens;
            }
        },
        ADICIONAR_CHILDREN_POR_ID(estado, { idComponente, novoItem }) {
            const componente = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: idComponente });
            if (componente) {
                const idxBotao = componente.children.findIndex((child) => child.componente === 'botaoEnviar');
                if (idxBotao !== -1) {
                    componente.children.splice(idxBotao, 0, novoItem);
                } else {
                    componente.children.push(novoItem);
                }
            }
        },
        INICIALIZAR_ESTRUTURA(estado, eventoId) {
            estado.estruturaPagina.children = [
                {
                    componenteId: v4(),
                    componente: 'pagina',
                    props: {
                        paddingHorizontal: 20,
                        paddingVertical: 0,
                        fonte: 'Roboto',
                        pesoFonte: '400',
                        corFundo: '',
                    },
                    children: [
                        {
                            componenteId: v4(),
                            componente: 'cabecalho',
                            props: { titulo: 'Cabeçalho' },
                            children: [],
                        },
                        {
                            componenteId: v4(),
                            componente: 'corpo',
                            props: { titulo: 'Corpo' },
                            children: [],
                        },
                        {
                            componenteId: v4(),
                            componente: 'rodape',
                            props: { titulo: 'Rodapé' },
                            children: [],
                        },
                    ],
                },
            ];

            estado.metadadosPagina.eventoId = eventoId;
            estado.metadadosPagina.landingPageId = v4();
            estado.metadadosPagina.urlPersonalizada = '';
        },
        REMOVER_COMPONENTE(estado, componenteId) {
            const removido = removerComponenteRecursivo({ componente: estado.estruturaPagina, id: componenteId });
            if (!removido) {
                throw new Error(`ID de componente inválido recebido: ${componenteId}`);
            }
        },
        DEFINIR_ID_ELEMENTO_SELECIONADO(estado, componenteId) {
            estado.idElementoSelecionado = componenteId;
        },
        DEFINIR_STATUS_ATUALIZACAO(estado, status) {
            const statusPermitidos = Object.keys(STATUS_ATUALIZACAO_ENUM);
            if (!statusPermitidos.includes(status)) {
                throw new Error(`Status de atualização inválido: ${status}. Valores permitidos: ${statusPermitidos.join(', ')}`);
            }
            estado.statusAtualizacao = STATUS_ATUALIZACAO_ENUM[status];
        },
        DEFINIR_STATUS_SALVAMENTO(estado, status) {
            const statusPermitidos = Object.keys(STATUS_SALVAMENTO_ENUM);
            if (!statusPermitidos.includes(status)) {
                throw new Error(`Status de salvamento inválido: ${status}. Valores permitidos: ${statusPermitidos.join(', ')}`);
            }
            estado.statusSalvamento = STATUS_SALVAMENTO_ENUM[status];
        },
        DEFINIR_STATUS_PREVIEW(estado, status) {
            if (!Object.keys(STATUS_PREVIEW_ENUM).includes(status)) {
                throw new Error(`Status de preview inválido: ${status}. Valores permitidos: ${Object.keys(STATUS_PREVIEW_ENUM).join(', ')}`);
            }
            estado.statusPreview = STATUS_PREVIEW_ENUM[status];
        },
        ATUALIZAR_METADADOS_PAGINA(estado, metadados) {
            estado.metadadosPagina = { ...estado.metadadosPagina, ...metadados };
        },
        EDITAR_METADADOS_PAGINA(estado, metadados) {
            // Essa mutation não é ignorada pelo plugin autosave, isso é necessário para evitar loop infinito de autosave
            estado.metadadosPagina = { ...estado.metadadosPagina, ...metadados };
        },
        DEFINIR_NOME_EVENTO(estado, nome) {
            estado.nomeEvento = nome;
        },
        ATUALIZAR_PROPS_COMPONENTE(estado, { idComponente, novasProps }) {
            const componente = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: idComponente });
            if (componente) {
                componente.props = mergeObjetosRecursivo(componente.props, novasProps);
            }
        },
        ATUALIZAR_ESTRUTURA_PAGINA(estado, estrutura) {
            estado.estruturaPagina = estrutura;
        },
        ATUALIZAR_DESIGN_FORMULARIO(estado, { formularioId, design }) {
            const formulario = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: formularioId });
            if (formulario && formulario.componente === 'formulario') {
                if (!formulario.props.design) {
                    formulario.props.design = {};
                }
                formulario.props.design = { ...formulario.props.design, ...design };
            }
        },
        ALTERAR_TIPO_COMPONENTE(estado, { idComponente, novoTipo }) {
            const componente = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: idComponente });
            if (componente) {
                componente.componente = novoTipo;
                componente.props = ComponenteFactory.obterPropsPadrao(novoTipo) || {};
            }
        },
        LIMPAR_CAMPOS_FORMULARIO(estado, formularioId) {
            const formulario = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: formularioId });
            if (formulario && formulario.children) {
                formulario.children.forEach((campo) => {
                    if (campo.props) {
                        campo.props.timestampLimpeza = Date.now();
                    }
                });
            }
        },
    },
    actions: {
        _definirStatusTemporario({ commit }, { mutation, status, duracao = 2500 }) {
            commit(mutation, status);

            setTimeout(() => {
                commit(mutation, 'INATIVO');
            }, duracao);
        },
        removerComponente({ commit }, componenteId) {
            commit('REMOVER_COMPONENTE', componenteId);
        },

        atualizarItensSecao({ commit }, { idSecao, novaListaItens }) {
            commit('DEFINIR_ITENS_SECAO', { idSecao, itens: novaListaItens });
        },

        definirChildrenPorId({ commit }, { idComponente, novaListaItens }) {
            commit('DEFINIR_CHILDREN_POR_ID', { idComponente, novaListaItens });
        },

        atualizarPropsComponente({ commit, state }, { idComponente, novasProps }) {
            const componente = encontrarChildrenPorId({ componente: state.estruturaPagina, valor: idComponente });
            if (componente == null) {
                return;
            }
            const propsComTimestamp = { ...novasProps, ultimaAtualizacao: Date.now() };
            const propsAtualizadas = mergeObjetosRecursivo(componente.props, propsComTimestamp);
            commit('ATUALIZAR_PROPS_COMPONENTE', { idComponente, novasProps: propsAtualizadas });
        },

        atualizarPropsPorNome({ commit, state }, { componente, novasProps }) {
            const elemento = encontrarChildrenPorComponente({ componente: state.estruturaPagina, valor: componente });
            if (elemento == null) {
                return;
            }
            const propsAtualizadas = mergeObjetosRecursivo(elemento.props, novasProps);
            commit('ATUALIZAR_PROPS_COMPONENTE', { idComponente: elemento.componenteId, novasProps: propsAtualizadas });
        },

        selecionarElemento({ commit }, idElemento) {
            commit('DEFINIR_ID_ELEMENTO_SELECIONADO', idElemento);
        },

        selecionarElementoPorComponente({ commit, state }, componente) {
            const elemento = encontrarChildrenPorComponente({ componente: state.estruturaPagina, valor: componente });
            if (elemento == null) return;
            commit('DEFINIR_ID_ELEMENTO_SELECIONADO', elemento.componenteId);
        },

        adicionarChildrenPorId({ commit }, { idComponente, novoItem }) {
            commit('ADICIONAR_CHILDREN_POR_ID', { idComponente, novoItem });
        },

        async atualizarLandingPage({ commit, state, dispatch }, eventoId) {
            commit('DEFINIR_STATUS_ATUALIZACAO', STATUS_ATUALIZACAO_ENUM.ATUALIZANDO);

            const estruturaNormalizada = normalizarEstrutura(JSON.parse(JSON.stringify(state.estruturaPagina)));
            const estruturaFinal = Array.isArray(estruturaNormalizada) ? estruturaNormalizada[0] : estruturaNormalizada;

            const payloadParaSalvar = {
                metadados: {
                    ...state.metadadosPagina,
                    eventoId: eventoId,
                },
                ...estruturaFinal,
            };

            const { data, error } = await tryCatch(EventosService.salvarLandingPage(payloadParaSalvar));

            if (error != null) {
                dispatch('_definirStatusTemporario', {
                    mutation: 'DEFINIR_STATUS_ATUALIZACAO',
                    status: STATUS_ATUALIZACAO_ENUM.ERRO,
                });
                return;
            }

            const metadadosDaAPI = data.data.metadados;
            const metadadosLocais = state.metadadosPagina;

            const metadadosMesclados = {
                ...metadadosDaAPI,
                titulo: metadadosLocais.titulo,
                urlPersonalizada: metadadosLocais.urlPersonalizada,
                seoConfig: metadadosLocais.seoConfig,
            };

            commit('ATUALIZAR_METADADOS_PAGINA', metadadosMesclados);
            dispatch('_definirStatusTemporario', {
                mutation: 'DEFINIR_STATUS_ATUALIZACAO',
                status: STATUS_ATUALIZACAO_ENUM.SUCESSO,
            });
            return data;
        },

        async salvarLandingPage({ commit, state, dispatch }) {
            commit('DEFINIR_STATUS_SALVAMENTO', STATUS_SALVAMENTO_ENUM.SALVANDO);

            if (this._cancelLandingPageAutosave) {
                this._cancelLandingPageAutosave();
            }

            const payloadParaPersistir = {
                metadados: {
                    ...state.metadadosPagina,
                    eventoId: state.metadadosPagina.eventoId,
                },
                ...state.estruturaPagina,
            };

            const { error } = await tryCatch(EventosService.persistirLandingPage(payloadParaPersistir));

            if (error?.response?.data?.errors) {
                const duplicacao = error.response.data.errors.find((m) => m.startsWith('DUPLICACAO_LANDING_PAGE:'));
                if (duplicacao) {
                    const landingPageId = duplicacao.split(':')[1];
                    console.log('Landing page duplicada detectada. Redirecionando para:', landingPageId);

                    commit('DEFINIR_DIRTY_STATE', false);
                    commit('DEFINIR_STATUS_SALVAMENTO', STATUS_SALVAMENTO_ENUM.INATIVO);

                    if (typeof window !== 'undefined' && window.EventBus) {
                        window.EventBus.$emit('landingPageDuplicada', landingPageId);
                    }

                    return { redirectTo: landingPageId };
                }
                return;
            }

            if (error != null) {
                console.error('Falha ao salvar e persistir a landing page:', error);
                dispatch('_definirStatusTemporario', {
                    mutation: 'DEFINIR_STATUS_SALVAMENTO',
                    status: STATUS_SALVAMENTO_ENUM.ERRO,
                });
                return;
            }

            commit('DEFINIR_DIRTY_STATE', false);

            dispatch('_definirStatusTemporario', {
                mutation: 'DEFINIR_STATUS_SALVAMENTO',
                status: STATUS_SALVAMENTO_ENUM.SUCESSO,
            });
        },

        async inicializarEditor({ commit, dispatch }, { eventoId, landingPageId, emBackground = false }) {
            const estrategias = {
                async nova({ commit, dispatch }, { eventoId, nomeEvento }) {
                    commit('DEFINIR_STATUS_CARREGAMENTO_EDITOR', STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.CARREGANDO);
                    commit('INICIALIZAR_ESTRUTURA', eventoId);

                    if (nomeEvento && nomeEvento !== 'Evento sem nome') {
                        await dispatch('atualizarMetadadosPorChave', {
                            chave: 'titulo',
                            valor: nomeEvento,
                        });
                    }

                    dispatch('_definirStatusTemporario', {
                        mutation: 'DEFINIR_STATUS_CARREGAMENTO_EDITOR',
                        status: STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.SUCESSO,
                    });
                },

                async existente({ dispatch }, { landingPageId }) {
                    commit('DEFINIR_STATUS_CARREGAMENTO_EDITOR', STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.CARREGANDO);
                    const { error } = await tryCatch(dispatch('obterLandingPagePorId', landingPageId));

                    if (error) {
                        dispatch('_definirStatusTemporario', {
                            mutation: 'DEFINIR_STATUS_CARREGAMENTO_EDITOR',
                            status: STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.ERRO,
                        });
                        throw new Error(`Falha ao carregar landing page: ${error.message}`);
                    }

                    dispatch('_definirStatusTemporario', {
                        mutation: 'DEFINIR_STATUS_CARREGAMENTO_EDITOR',
                        status: STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.SUCESSO,
                    });
                },
            };

            if (!emBackground) {
                commit('DEFINIR_STATUS_CARREGAMENTO_EDITOR', 'CARREGANDO');
            }

            const { data: nomeEvento, error } = await tryCatch(dispatch('carregarNomeEvento', eventoId));

            if (error) {
                console.error('Erro ao carregar dados iniciais:', error);
                if (!emBackground) {
                    commit('DEFINIR_STATUS_CARREGAMENTO_EDITOR', 'ERRO');
                }
                return;
            }

            const estrategia = landingPageId ? estrategias.existente : estrategias.nova;
            const { error: erroEstrategia } = await tryCatch(estrategia({ commit, dispatch }, { eventoId, landingPageId, nomeEvento }));

            if (erroEstrategia) {
                console.error('Erro durante inicialização:', erroEstrategia);
                if (!emBackground) {
                    commit('DEFINIR_STATUS_CARREGAMENTO_EDITOR', 'ERRO');
                }
                return;
            }

            if (nomeEvento) {
                commit('ATUALIZAR_METADADOS_PAGINA', { titulo: nomeEvento });
            }

            if (!emBackground) {
                commit('DEFINIR_STATUS_CARREGAMENTO_EDITOR', 'SUCESSO');
            }
        },

        async carregarNomeEvento({ commit }, eventoId) {
            const { data, error } = await tryCatch(EventosService.obterPorId(eventoId));

            if (error) {
                console.error('Erro ao carregar nome do evento:', error);
                return;
            }
            const nomeEvento = data?.data.nome ?? 'Evento sem nome';
            commit('DEFINIR_NOME_EVENTO', nomeEvento);
            return nomeEvento;
        },

        async obterLandingPagePorId({ commit, state }, landingPageId) {
            const { data, error } = await tryCatch(EventosService.obterLandingPagePorId(landingPageId));
            if (error || data.data == null) {
                return null;
            }
            commit('ATUALIZAR_METADADOS_PAGINA', data.data.metadados);
            commit('ATUALIZAR_ESTRUTURA_PAGINA', {
                ...state.estruturaPagina,
                children: data.data.children,
            });

            return data.data;
        },

        async obterLandingPagePorEventoId({ commit }, eventoId) {
            const { data, error } = await tryCatch(EventosService.obterLandingPagePorEventoId(eventoId));
            if (error || !data?.data) {
                return null;
            }
            commit('ATUALIZAR_METADADOS_PAGINA', data.data.metadados);
            if (data.data.estruturaPagina) {
                commit('ATUALIZAR_ESTRUTURA_PAGINA', data.data.estruturaPagina);
            }
            return data.data;
        },

        atualizarMetadadosPorChave({ commit, state }, { chave, valor }) {
            if (chave == null) throw new Error('Chave não pode ser nula');

            const newMetadata = JSON.parse(JSON.stringify(state.metadadosPagina));

            if (['palavrasChave', 'descricao', 'imgCompartilhamento'].includes(chave)) {
                newMetadata.seoConfig[chave] = valor;
            } else {
                newMetadata[chave] = valor;
            }

            commit('EDITAR_METADADOS_PAGINA', newMetadata);
        },

        atualizarDesignFormulario({ commit, dispatch, state }, { formularioId, design }) {
            if (design.botaoLimparAlinhamento === 'end') {
                const formulario = encontrarChildrenPorId({ componente: state.estruturaPagina, valor: formularioId });
                const botaoPrincipal = formulario?.children?.find((c) => c.componente === 'botaoEnviar');
                if (botaoPrincipal) {
                    dispatch('atualizarPropsComponente', {
                        idComponente: botaoPrincipal.componenteId,
                        novasProps: { alinhamento: 'end' },
                    });
                }
            }
            commit('ATUALIZAR_DESIGN_FORMULARIO', { formularioId, design });
        },

        async publicarLandingPage({ commit, state }) {
            const landingPageId = state.metadadosPagina.landingPageId;
            if (!landingPageId) {
                console.error('ID da Landing Page não encontrado.', state.metadadosPagina);
                throw new Error('Não foi possível publicar: ID da Landing Page não encontrado.');
            }

            const { error } = await tryCatch(EventosService.publicarLandingPage(landingPageId));

            if (error) {
                console.error('Falha ao publicar a landing page:', error);
                throw error;
            }

            commit('ATUALIZAR_METADADOS_PAGINA', { status: 'Publicado' });
        },
        alterarTipoComponente({ commit }, { idComponente, novoTipo }) {
            commit('ALTERAR_TIPO_COMPONENTE', { idComponente, novoTipo });
        },

        limparCamposFormulario({ commit, state }) {
            const formulario = encontrarChildrenPorComponente({ componente: state.estruturaPagina, valor: 'formulario' });
            if (formulario) {
                commit('LIMPAR_CAMPOS_FORMULARIO', formulario.componenteId);
            }
        },
    },
    getters: {
        obterIdDaLandingPage: (estado) => estado.metadadosPagina.landingPageId,
        obterChildrenPorId: (estado) => (id) => {
            const componente = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: id });
            return componente?.children ?? [];
        },
        obterCampoMetadadoPorChave: (estado) => (chave) => {
            const { metadadosPagina } = estado;
            if (!metadadosPagina) return null;

            if (['palavrasChave', 'descricao', 'imgCompartilhamento'].includes(chave)) {
                return metadadosPagina.seoConfig?.[chave];
            }

            return metadadosPagina[chave];
        },
        obterElementoPorComponente: (estado) => (nomeComponente) => {
            const componente = encontrarChildrenPorComponente({ componente: estado.estruturaPagina, valor: nomeComponente });
            return componente ?? [];
        },
        obterDirtyState: (estado) => estado.isDirty,
        obterIdElementoSelecionado: (estado) => estado.idElementoSelecionado,
        obterStatusSalvamento: (estado) => estado.statusSalvamento,
        obterStatusAtualizacao: (estado) => estado.statusAtualizacao,
        obterStatusPreview: (estado) => estado.statusPreview,
        obterStatusCarregamentoEditorLandingPage: (estado) => estado.statusCarregamentoEditorLandingPage,
        obterElementoSelecionado: (estado, getters) => {
            const idElementoSelecionado = estado.idElementoSelecionado;

            if (idElementoSelecionado == null) return null;

            if (idElementoSelecionado === 'botaoLimpar-proxy') {
                const design = getters.designFormularioAtivo;
                return {
                    componenteId: 'botaoLimpar-proxy',
                    componente: 'botaoLimparCampos',
                    props: {
                        alinhamento: design.botaoLimparAlinhamento || 'end',
                        cor: design.botaoLimparCor || '#6c757d',
                    },
                };
            }

            const componente = encontrarChildrenPorId({ componente: estado.estruturaPagina, valor: idElementoSelecionado });
            return componente;
        },
        obterNomeEvento: (estado) => estado.nomeEvento,
        formularioAtivo: (estado) => {
            function encontrarFormulario(componentes) {
                for (let componente of componentes) {
                    if (componente.componente === 'formulario') {
                        return componente;
                    }
                    if (componente.children) {
                        const resultado = encontrarFormulario(componente.children);
                        if (resultado) return resultado;
                    }
                }
                return null;
            }

            return encontrarFormulario([estado.estruturaPagina]);
        },
        designFormularioAtivo: (estado, getters) => {
            const formulario = getters.formularioAtivo;
            if (!formulario)
                return {
                    imagemFundo: '',
                    corFundo: '#ffffff',
                    tipografia: 'Arial',
                };

            return (
                formulario.props.design || {
                    imagemFundo: '',
                    corFundo: '#ffffff',
                    tipografia: 'Arial',
                }
            );
        },
        temFormulario: (estado) => {
            return encontrarChildrenPorComponente({ componente: estado.estruturaPagina, valor: 'formulario' }) != null;
        },
    },
};

export default landingPageModule;
